'use client'

import { clsx } from 'clsx'
import React from 'react'
import { createContext, useContext, useMemo, useState, useEffect } from 'react'
import { Link } from './../link/Link.component'
import { Checkbox } from './../checkbox/Checkbox.component'
import { Button } from './../button/Button.component'
import { Dialog, DialogActions, DialogBody, DialogTitle } from './../dialog/Dialog.component'
import { atom, useAtom } from 'jotai'
import { CogIcon } from '@heroicons/react/24/outline'

type RowSize = 'small' | 'medium' | 'large'

type TableContextType = {
  bleed: boolean
  dense: boolean
  grid: boolean
  striped: boolean
  rowSize: RowSize
  setRowSize: (size: RowSize) => void
  hiddenColumns: Set<string>
  setHiddenColumns: React.Dispatch<React.SetStateAction<Set<string>>>
  registerColumn: (id: string, label: string, defaultVisible?: boolean) => void
  columns: { id: string; label: string }[]
  openSettings: () => void
}

const TableContext = createContext<TableContextType>({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
  rowSize: 'medium',
  setRowSize: () => {
    throw new Error('setRowSize called outside of Table provider')
  },
  hiddenColumns: new Set<string>(),
  setHiddenColumns: () => {
    throw new Error('setHiddenColumns called outside of Table provider')
  },
  registerColumn: () => {
    throw new Error('registerColumn called outside of Table provider')
  },
  columns: [],
  openSettings: () => {
    throw new Error('openSettings called outside of Table provider')
  },
})

type TableSettings = { rowSize: RowSize; hiddenColumns: string[] }
type TableSettingsMap = Record<string, TableSettings>

const STORAGE_KEY = 'table_settings.v1'
const initialSettings: TableSettingsMap = (() => {
  if (typeof window === 'undefined') return {}
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY)
    return raw ? (JSON.parse(raw) as TableSettingsMap) : {}
  } catch {
    return {}
  }
})()

const tableSettingsAtom = atom<TableSettingsMap>(initialSettings)
const tableSettingsOpenAtom = atom<Record<string, boolean>>({})

export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  fit = false,
  className,
  children,
  ...props
}: { bleed?: boolean; dense?: boolean; grid?: boolean; striped?: boolean; fit?: boolean; rowSize?: RowSize; tableId?: string } & React.ComponentPropsWithoutRef<'div'>) {
  const [settingsMap, setSettingsMap] = useAtom(tableSettingsAtom)
  const [openMap, setOpenMap] = useAtom(tableSettingsOpenAtom)
  const settingsKey = useMemo<string>(() => {
    const pid = (props as any).id as string | undefined
    const dataId = (props as any)['data-table-id'] as string | undefined
    const fallback = typeof window !== 'undefined' ? window.location.pathname : 'default'
    return pid ?? dataId ?? fallback
  }, [props])
  const ensureSettings = (m: TableSettingsMap): TableSettingsMap => {
    if (m[settingsKey]) return m
    const next: TableSettingsMap = { ...m, [settingsKey]: { rowSize: (dense ? 'small' : 'medium') as RowSize, hiddenColumns: [] as string[] } }
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {
      console.error('Error setting table settings', STORAGE_KEY, next)
    }
    return next
  }
  const persist = (next: TableSettingsMap) => {
    setSettingsMap(next)
    try { window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next)) } catch {
      console.error('Error persisting table settings', STORAGE_KEY, next)
    }
  }
  const hadExistingSettings = Boolean(settingsMap[settingsKey])
  const ensuredMap = hadExistingSettings ? settingsMap : ensureSettings(settingsMap)
  const currentSettings: TableSettings = ensuredMap[settingsKey] ?? { rowSize: (dense ? 'small' : 'medium') as RowSize, hiddenColumns: [] as string[] }
  const hiddenColumns = useMemo(() => new Set<string>(currentSettings.hiddenColumns), [currentSettings.hiddenColumns])
  const setRowSize = (size: RowSize) => {
    const next = { ...settingsMap, [settingsKey]: { ...currentSettings, rowSize: size } }
    persist(next)
  }
  const setHiddenColumns: React.Dispatch<React.SetStateAction<Set<string>>> = (updater) => {
    const inputSet: Set<string> = typeof updater === 'function' ? (updater as (prev: Set<string>) => Set<string>)(hiddenColumns) : updater
    const arr: string[] = Array.from(inputSet.values())
    const next: TableSettingsMap = { ...settingsMap, [settingsKey]: { ...currentSettings, hiddenColumns: arr } }
    persist(next)
  }
  const [columns, setColumns] = useState<{ id: string; label: string }[]>([])

  const registerColumn = (id: string, label: string, defaultVisible: boolean = true) => {
    setColumns((prev) => (prev.some((c) => c.id === id) ? prev : [...prev, { id, label }]))
    if (!hadExistingSettings && defaultVisible === false) {
      setHiddenColumns((prev) => {
        const next = new Set(prev)
        next.add(id)
        return next
      })
    }
  }

  const contextValue: TableContextType = useMemo(() => ({
    bleed,
    dense,
    grid,
    striped,
    rowSize: currentSettings.rowSize,
    setRowSize,
    hiddenColumns,
    setHiddenColumns,
    registerColumn,
    columns,
    openSettings: () => setOpenMap({ ...openMap, [settingsKey]: true }),
  }), [bleed, dense, grid, striped, currentSettings.rowSize, hiddenColumns, columns, openMap, settingsKey])

  return (
    <TableContext.Provider value={contextValue}>
      <div className="flow-root">
        <div {...props} className={clsx(className, '-mx-[--gutter] overflow-x-auto whitespace-nowrap relative')}>
          {/* settings button intentionally not rendered here; render near pagination via rightAccessory */}
          <div className={clsx('inline-block align-middle', !fit && 'min-w-full', !bleed && 'sm:px-[--gutter]')}>
            <table className={clsx('text-left text-sm/6', fit ? 'w-fit' : 'min-w-full')}>{children}</table>
          </div>
        </div>
      </div>

      <TableSettings open={Boolean(openMap[settingsKey])} onClose={() => setOpenMap({ ...openMap, [settingsKey]: false })} />
    </TableContext.Provider>
  )
}

export function TableHead({ className, ...props }: React.ComponentPropsWithoutRef<'thead'>) {
  return <thead className={clsx(className, 'text-zinc-500 dark:text-zinc-400')} {...props} />
}

export function TableBody(props: React.ComponentPropsWithoutRef<'tbody'>) {
  return <tbody {...props} />
}

const TableRowContext = createContext<{ href?: string; target?: string; title?: string }>({
  href: undefined,
  target: undefined,
  title: undefined,
})

export function TableRow({
  href,
  target,
  title,
  className,
  children,
  setRowRef,
  ...props
}: { href?: string; target?: string; title?: string, setRowRef?: any } & React.ComponentPropsWithoutRef<'tr'>) {
  const { striped } = useContext(TableContext)

  return (
    <TableRowContext.Provider value={{ href, target, title } as React.ContextType<typeof TableRowContext>}>
      <tr
        {...props}
          ref={setRowRef}
        className={clsx(
          className,
          href &&
            'has-[[data-row-link][data-focus]]:outline has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/[2.5%]',
          striped && 'even:bg-zinc-950/[2.5%] dark:even:bg-white/[2.5%]',
          href && striped && 'hover:bg-zinc-950/5 dark:hover:bg-white/5',
          href && !striped && 'hover:bg-zinc-950/[2.5%] dark:hover:bg-white/[2.5%]'
        )}
      >
        {children}
      </tr>
    </TableRowContext.Provider>
  )
}

export function TableHeader({ columnId, columnLabel, defaultVisible = true, className, children, ...props }: React.ComponentPropsWithoutRef<'th'> & { columnId?: string; columnLabel?: string; defaultVisible?: boolean }) {
  const { bleed, grid, hiddenColumns, registerColumn } = useContext(TableContext)

  const computedLabel = useMemo(() => (
    columnLabel ?? (typeof children === 'string' ? (children as string) : (columnId || ''))
  ), [columnLabel, children, columnId])

  useEffect(() => {
    if (columnId) {
      registerColumn(columnId, computedLabel, defaultVisible)
    }
  }, [columnId, computedLabel, defaultVisible, registerColumn])

  return (
    <th
      {...props}
      className={clsx(
        className,
        'border-b border-b-zinc-950/10 px-4 py-2 font-medium first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        !bleed && 'sm:first:pl-2 sm:last:pr-2',
        columnId && hiddenColumns.has(columnId) && 'hidden'
      )}
    >
      {children}
    </th>
  )
}

export function TableCell({ type, columnId, className, children, ...props }: { type?: 'light' | 'main' | 'link'; columnId?: string } & React.ComponentPropsWithoutRef<'td'>) {
  const { bleed, grid, striped, rowSize, hiddenColumns } = useContext(TableContext)
  const { href, target, title } = useContext(TableRowContext)
  const [cellRef, setCellRef] = useState<HTMLElement | null>(null);

  let appendedClasses = '';

  if (type === 'light') {
    appendedClasses = 'font-light text-gray-600 dark:text-gray-400';
  } else if (type === 'main') {
    appendedClasses = 'font-medium text-gray-900 dark:text-gray-200';
  } else if (type === 'link') {
    appendedClasses = 'font-medium text-cyan-700 dark:text-cyan-400 cursor-pointer underline hover:text-cyan-500 dark:hover:text-cyan-300'
  }

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        appendedClasses,
        'relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]',
        !striped && 'border-b border-zinc-950/5 dark:border-white/5',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        rowSize === 'small' ? 'py-1' : rowSize === 'large' ? 'py-6' : 'py-4',
        !bleed && 'sm:first:pl-2 sm:last:pr-2',
        columnId && hiddenColumns.has(columnId) && 'hidden'
      )}
    >
      {href && (
        <Link
          data-row-link
          href={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-none"
        />
      )}
      {children}
    </td>
  )
}

export function TableCheckbox({
  checked,
  onChange,
  disabled,
  ariaLabel = 'Select row',
  className,
  ...props
}: {
  checked: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  ariaLabel?: string
} & Omit<React.ComponentPropsWithoutRef<'td'>, 'onChange'>) {
  const { bleed, grid } = useContext(TableContext)

  return (
    <td
      {...props}
      className={clsx(
        className,
        'relative px-4 first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))]',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        !bleed && 'sm:first:pl-2 sm:last:pr-2'
      )}
    >
      <div className="flex items-center">
        <Checkbox
          checked={checked}
          onChange={onChange}
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </td>
  )
}

export function TableHeadCheckbox({
  checked,
  indeterminate = false,
  onChange,
  disabled,
  ariaLabel = 'Select all rows',
  className,
  ...props
}: {
  checked: boolean
  indeterminate?: boolean
  onChange?: (checked: boolean) => void
  disabled?: boolean
  ariaLabel?: string
} & Omit<React.ComponentPropsWithoutRef<'th'>, 'onChange'>) {
  const { bleed, grid } = useContext(TableContext)

  return (
    <th
      {...props}
      className={clsx(
        className,
        'border-b border-b-zinc-950/10 px-4 py-2 font-medium first:pl-[var(--gutter,theme(spacing.2))] last:pr-[var(--gutter,theme(spacing.2))] dark:border-b-white/10',
        grid && 'border-l border-l-zinc-950/5 first:border-l-0 dark:border-l-white/5',
        !bleed && 'sm:first:pl-2 sm:last:pr-2'
      )}
    >
      <div className="flex items-center">
        <Checkbox
          checked={checked}
          indeterminate={indeterminate}
          onChange={onChange}
          disabled={disabled}
          aria-label={ariaLabel}
          onClick={(e) => e.stopPropagation()}
        />
      </div>
    </th>
  )
}

export function TableSettings({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { rowSize, setRowSize, columns, hiddenColumns, setHiddenColumns } = useContext(TableContext)

  const isVisible = (id: string) => !hiddenColumns.has(id)
  const setVisible = (id: string, visible: boolean) => {
    setHiddenColumns((prev) => {
      const next = new Set(prev)
      if (visible) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Table settings</DialogTitle>
      <DialogBody>
        <div className="space-y-6">
          <div>
            <div className="mb-2 font-medium text-zinc-700 dark:text-zinc-200">Row size</div>
            <div className="flex gap-2">
              {rowSize === 'small' ? (
                <Button color="zinc" onClick={() => setRowSize('small')}>Small</Button>
              ) : (
                <Button outline onClick={() => setRowSize('small')}>Small</Button>
              )}
              {rowSize === 'medium' ? (
                <Button color="zinc" onClick={() => setRowSize('medium')}>Medium</Button>
              ) : (
                <Button outline onClick={() => setRowSize('medium')}>Medium</Button>
              )}
              {rowSize === 'large' ? (
                <Button color="zinc" onClick={() => setRowSize('large')}>Large</Button>
              ) : (
                <Button outline onClick={() => setRowSize('large')}>Large</Button>
              )}
            </div>
          </div>

          <div>
            <div className="mb-2 font-medium text-zinc-700 dark:text-zinc-200">Columns</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {columns.length === 0 ? (
                <div className="text-sm text-zinc-500 dark:text-zinc-400">No columns registered.</div>
              ) : (
                columns.map((c) => (
                  <label key={c.id} className="inline-flex items-center gap-2">
                    <Checkbox checked={isVisible(c.id)} onChange={(checked) => setVisible(c.id, checked)} aria-label={`Toggle ${c.label}`} />
                    <span className="text-sm text-zinc-800 dark:text-zinc-200">{c.label}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </div>
      </DialogBody>
      <DialogActions>
        <Button color="light" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export function TableSettingsButton({ className }: { className?: string }) {
  const [, setOpenMap] = useAtom(tableSettingsOpenAtom)
  const settingsKey = React.useMemo<string>(() => {
    const fallback = typeof window !== 'undefined' ? window.location.pathname : 'default'
    return fallback
  }, [])
  return (
    <Button plain onClick={() => setOpenMap((prev) => ({ ...(prev ?? {}), [settingsKey]: true }))} aria-label="Open table settings" className={className}>
      <CogIcon className="w-5 h-5" />
    </Button>
  )
}
